<template>
  <v-container fluid class="pa-4">
    <v-row justify="space-between" align="center" class="mb-6">
      <v-col cols="12" sm="7">
        <h1 class="text-h4 font-weight-bold primary--text">
          <v-icon large color="primary" class="mr-3">mdi-account-multiple</v-icon>
          {{ $t('golf_players_summary') || 'Resumen de Jugadores' }}
        </h1>
      </v-col>
      <v-col cols="12" sm="5" class="text-sm-right">
        <v-btn color="primary" rounded outlined @click="loadSummary" :loading="loading">
          <v-icon left small>mdi-refresh</v-icon>
          {{ $t('actualizar') || 'Actualizar' }}
        </v-btn>
      </v-col>
    </v-row>

    <v-row class="mb-6" dense>
      <v-col cols="12" sm="6" md="3">
        <v-card class="pa-4" outlined>
          <div class="caption grey--text">Rondas registradas</div>
          <div class="text-h4 font-weight-bold primary--text">{{ summary.totalRounds }}</div>
        </v-card>
      </v-col>
    </v-row>

    <v-card class="elevation-2">
      <v-card-title>
        {{ $t('golf_players_summary') || 'Resumen de Jugadores' }}
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="players"
          :loading="loading"
          :no-data-text="$t('no_records_yet') || 'Sin resultados'"
          class="elevation-0"
        >
          <template v-slot:item.best_score_vs_par="{ item }">
            <span :class="getScoreClass(item.best_score_vs_par)">
              {{ item.best_score_vs_par > 0 ? '+' : '' }}{{ item.best_score_vs_par }}
            </span>
          </template>
          <template v-slot:item.last_played="{ item }">
            {{ formatDate(item.last_played) }}
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import { aggregatePlayers, fetchBulkGolfScorecards, getDateRange } from '@/utils/golfScorecards'

export default {
  name: 'GolfPlayersSummary',
  data () {
    return {
      loading: false,
      players: [],
      headers: [
        { text: 'Jugador', value: 'player_name', sortable: false },
        { text: 'Rondas', value: 'rounds_count', sortable: false },
        { text: 'Handicap actual', value: 'latest_handicap', sortable: false },
        { text: 'Mejor neto', value: 'best_net_score', sortable: false },
        { text: 'Mejor vs par', value: 'best_score_vs_par', sortable: false },
        { text: 'Última ronda', value: 'last_played', sortable: false },
        { text: 'Salidas', value: 'courses', sortable: false }
      ]
    }
  },
  computed: {
    ...mapGetters(['getProfile']),
    housingUnitId () {
      return this.getProfile.idHousingUnit || localStorage.getItem('idHousingUnit')
    },
    currentLanguage () {
      return (this.$i18n && this.$i18n.locale) || 'es'
    },
    summary () {
      if (!this.players.length) {
        return {
          totalRounds: 0,
          uniquePlayers: 0,
          bestNetScore: 0,
          avgHandicap: 0
        }
      }

      const totalRounds = this.players.reduce((sum, player) => sum + player.rounds_count, 0)
      const handicapTotal = this.players.reduce((sum, player) => sum + player.avg_handicap, 0)
      const validNetScores = this.players
        .map(p => p.best_net_score)
        .filter(score => typeof score === 'number' && !isNaN(score))
      const bestNetScore = validNetScores.length > 0 ? Math.min(...validNetScores) : 0

      return {
        totalRounds,
        uniquePlayers: this.players.length,
        bestNetScore,
        avgHandicap: Number((handicapTotal / this.players.length).toFixed(1))
      }
    }
  },
  mounted () {
    this.loadSummary()
  },
  methods: {
    async loadSummary () {
      if (!this.housingUnitId) {
        return
      }

      this.loading = true
      try {
        const range = getDateRange(180, 1)
        const dataset = await fetchBulkGolfScorecards({
          housingUnitId: this.housingUnitId,
          language: this.currentLanguage,
          startDate: range.start,
          endDate: range.end
        })
        this.players = aggregatePlayers(dataset.userScores)
      } catch (error) {
        console.error('Error fetching players summary:', error)
      } finally {
        this.loading = false
      }
    },
    formatDate (date) {
      if (!date) return '--'
      return new Date(date).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    getScoreClass (scoreVsPar) {
      if (scoreVsPar < -1) return 'primary--text'
      if (scoreVsPar === -1) return 'success--text'
      if (scoreVsPar === 0) return 'info--text'
      if (scoreVsPar === 1) return 'warning--text'
      return 'error--text'
    }
  }
}
</script>
